import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import PublicHeader from '@/components/ui/public-header';
import Footer from '@/components/ui/footer';
import { PLANS, CURRENCY, BillingInterval } from '@/lib/pricing';
import { Loader2 } from 'lucide-react';
import { apiService } from '@/lib/services/api';
import { useAppConfig } from '@/lib/hooks/useAppConfig';

interface StripePrice {
  id: string;
  productId: string;
  unitAmount: number;
  currency: string;
  interval: string;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { waitingListMode, isLoading: configLoading } = useAppConfig();
  const [prices, setPrices] = useState<StripePrice[]>([]);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!configLoading && waitingListMode) {
      navigate('/', { replace: true });
    }
  }, [waitingListMode, configLoading, navigate]);

  useEffect(() => {
    async function fetchStripeData() {
      try {
        const [pricesRes, productsRes] = await Promise.all([
          fetch('/api/stripe/prices'),
          fetch('/api/stripe/products'),
        ]);

        if (pricesRes.ok && productsRes.ok) {
          const pricesData = await pricesRes.json();
          const productsData = await productsRes.json();
          setPrices(pricesData);
          setProducts(productsData);
        }
      } catch (error) {
        console.error('Error fetching Stripe data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchStripeData();
  }, []);

  if (configLoading || waitingListMode) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  // Map Stripe products to our plan keys
  const starterProduct = products.find((product) => product.name === 'Starter');
  const proProduct = products.find((product) => product.name === 'Pro');

  const getStripePriceId = (productId: string | undefined, interval: string) => {
    return prices.find((price) => price.productId === productId && price.interval === interval)?.id;
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="-mt-10 z-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-medium text-foreground mb-2 tracking-tight">
              Pricing
            </h1>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-surface-track rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  billingInterval === 'monthly'
                    ? 'bg-surface-raised text-foreground shadow-sm'
                    : 'text-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('annual')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  billingInterval === 'annual'
                    ? 'bg-surface-raised text-foreground shadow-sm'
                    : 'text-foreground hover:text-foreground'
                }`}
              >
                Annual
                <span className="ml-2 inline-block bg-success-surface-strong text-success-foreground-deep text-xs font-semibold px-2 py-1 rounded">
                  Save 20%
                </span>
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 max-w-3xl mx-auto mb-20">
            {/* Starter Plan */}
            <PricingCard
              name={PLANS.starter.name}
              price={PLANS.starter.pricing[billingInterval].price}
              interval={billingInterval}
              trialDays={PLANS.starter.trialDays}
              description={PLANS.starter.description}
              features={[...PLANS.starter.features]}
              priceId={getStripePriceId(starterProduct?.id, billingInterval)}
              buttonText="Start free trial"
              loading={loading}
              savings={billingInterval === 'annual' ? PLANS.starter.pricing.annual.savings : undefined}
            />

            {/* Pro Plan */}
            <PricingCard
              name={PLANS.pro.name}
              price={PLANS.pro.pricing[billingInterval].price}
              interval={billingInterval}
              trialDays={PLANS.pro.trialDays}
              description={PLANS.pro.description}
              features={[...PLANS.pro.features]}
              priceId={getStripePriceId(proProduct?.id, billingInterval)}
              buttonText="Start free trial"
              loading={loading}
              savings={billingInterval === 'annual' ? PLANS.pro.pricing.annual.savings : undefined}
            />
          </div>

          {/* FAQ Section */}
          <div className="text-center">
            <h2 className="text-3xl font-medium text-foreground mb-12">
              FAQ
            </h2>
            <div className="max-w-2xl mx-auto space-y-8">
              <div>
                <h3 className="font-medium text-foreground mb-2 text-lg">Can I change plans anytime?</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2 text-lg">What's included in all plans?</h3>
                <p className="text-muted-foreground leading-relaxed">
                  All plans include unlimited email accounts, email warmup, sequences with scheduling, and analytics. Pro adds more email enrichments, contacts, and advanced features.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2 text-lg">Do contact limits reset monthly?</h3>
                <p className="text-muted-foreground leading-relaxed">
                  No, uploaded contact limits don't reset monthly. They represent the total number of contacts you can have active in campaigns at any time.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2 text-lg">How many emails can I send per account?</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We recommend sending 30-50 emails per day per account for optimal deliverability. With unlimited accounts, you can scale your volume significantly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function PricingCard({
  name,
  price,
  interval,
  trialDays,
  description,
  features,
  priceId,
  buttonText = 'Start free trial',
  loading = false,
  savings,
}: {
  name: string;
  price: number;
  interval: string;
  trialDays: number;
  description: string;
  features: string[];
  priceId?: string | null;
  buttonText?: string;
  loading?: boolean;
  savings?: number;
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const handleCheckout = async () => {
    if (!priceId) {
      navigate('/sign-up');
      return;
    }

    setSubmitting(true);
    try {
      const data = await apiService.post<{ url: string }>('/api/stripe/checkout', { priceId });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      navigate(`/sign-up?priceId=${priceId}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="relative p-8 rounded-card transition-all duration-200 flex flex-col h-full">
      <CardHeader className="px-0 pt-0 pb-6">
        <CardTitle className="text-2xl font-medium mb-1 text-foreground">
          {name}
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          {description}
        </CardDescription>
      </CardHeader>

      <CardContent className="px-0 pb-8 flex-1">
        <div className="mb-8">
          <div className="flex items-baseline">
            <span className="text-5xl font-medium text-foreground">
              {CURRENCY.symbol}{price / 100}
            </span>
            <span className="text-xl ml-1 text-muted-foreground">
              /{interval === 'monthly' ? 'month' : 'year'}
            </span>
          </div>
          <p className="text-sm mt-2 text-muted-foreground">
            {trialDays}-day free trial
          </p>
          {savings && (
            <p className="text-sm font-medium mt-1 text-success-foreground">
              Save {CURRENCY.symbol}{savings / 100} per year
            </p>
          )}
        </div>

        <ul className="space-y-4">
          {features.map((feature, index) => (
            <li key={index}>
              <span className="leading-6 text-sm text-muted-foreground">
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter className="px-0 pt-0 mt-auto">
        <Button
          onClick={handleCheckout}
          disabled={loading || submitting}
          className="w-full h-12 rounded-lg font-medium"
          variant="primary"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            buttonText
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

